import type {
  AppSwitchBarItem,
  AppSwitchCluster,
} from '@repo/studio-components/top-app-bar/components/app-switch-bar'
import { useAppSwitchItems } from '@repo/studio-components/top-app-bar/components/app-switch-bar'
import {
  ClockRefreshIcon,
  FaceIdSquare,
  VolumeMaxIcon,
  Cube01Icon,
  Image01Icon,
  ScissorsCut02Icon,
  Tv03Icon,
  Certificate02Icon,
  // oxlint-disable-next-line cf-web/banned-imports
} from 'primitives/icons'
import { DropperIcon } from '@repo/studio-design-system-ui-icons/editor/dropper'
import { Contrast02Icon } from '@repo/studio-design-system-ui-icons/editor/contrast-02'
import { Announcement03Icon } from '@repo/studio-design-system-ui-icons/alerts-feedback/announcement-03'
import { Film01Icon } from '@repo/studio-design-system-ui-icons/media-devices/film-01'
import { ImageUserIcon } from '@repo/studio-design-system-ui-icons/images/image-user'
import { Expand06Icon } from '@repo/studio-design-system-ui-icons/arrows/expand-06'
import { InPaintingIcon } from '@repo/studio-design-system-ui-icons/general/in-painting'
import * as React from 'react'
import { useFeatureIsOn } from 'growthbook'
import { useFavoriteToolTypes } from '@repo/studio-components/tools/hooks/use-favorite-tool-types'
import { ROUTES } from '@repo/studio-utils/routes'
import {
  navigationSectionClusterIds,
  useStudioNavigationConfig,
  useStudioTranslate,
} from '@repo/studio-config/studio-config-context'
import type {
  CategoryClusterEntry,
  CategoryNavigationSection,
  NavigationFooterItem,
  NavigationPromo,
  NavigationSectionConfig,
  NavigationToolGroupConfig,
} from '@repo/studio-config/studio-config-context'
import { useIsFreemode } from '@repo/studio-config/app-source'

/**
 * Cluster id usable in the left navigation. Real `AppSwitchCluster` ids drive
 * the standard clusters; host-configured `extraToolGroups` contribute synthetic
 * string ids that have no `CLUSTER_ROUTES` entry or category page — they only
 * group existing tools into their own navigation row.
 */
export type NavigationClusterId = AppSwitchCluster | (string & {})

function clusterEntries(clusters: AppSwitchCluster[]): CategoryClusterEntry[] {
  return clusters.map(cluster => ({ kind: 'cluster', cluster }))
}

// Video leads Image to match Freemode's order (STU-8918); keep both in sync.
export const AI_SECTION_CLUSTERS: AppSwitchCluster[] = ['video', 'image', 'audio', '3d']
export const UTILITIES_SECTION_CLUSTERS: AppSwitchCluster[] = [
  'marketing',
  'utilities',
  'entertainment',
  'crafting',
]

export const NAVIGATION_SECTIONS: NavigationSectionConfig[] = [
  {
    kind: 'home',
  },
  {
    kind: 'pinned',
  },
  {
    kind: 'category',
    category: 'ai',
    entries: clusterEntries(AI_SECTION_CLUSTERS),
  },
  {
    kind: 'category',
    category: 'more',
    entries: clusterEntries(UTILITIES_SECTION_CLUSTERS),
  },
]

export const NAVIGATION_FOOTER: NavigationFooterItem[] = [
  { kind: 'mcp' },
  { kind: 'api-platform' },
  { kind: 'characters' },
  { kind: 'dark-mode' },
  { kind: 'language' },
  { kind: 'menu' },
]

export const FEATURE_SHOW_MCP_NAV_BUTTON = 'show-mcp-nav-button'
export const FEATURE_DIRECT_LINK_MARKETING_WORKSPACE = 'STUGROW-23-direct-link-marketing-workspace'

export function useNavigationFooter(): NavigationFooterItem[] {
  const items = useStudioNavigationConfig()?.footer ?? NAVIGATION_FOOTER
  const isMcpEnabled = useFeatureIsOn(FEATURE_SHOW_MCP_NAV_BUTTON)
  return isMcpEnabled ? items : items.filter(item => item.kind !== 'mcp')
}

export function useNavigationPromo(): NavigationPromo {
  return useStudioNavigationConfig()?.promo ?? 'coins'
}

interface ClusterDefinitionBase {
  cluster: NavigationClusterId
  label: string
  icon: React.ReactNode
  allLabel: string
  /**
   * Set for a host-configured tool group that is unavailable on this host. The
   * cluster stays visible but renders a message-only panel (see `disabledMessage`)
   * and contributes no tool rows.
   */
  disabled?: boolean
  /** Message shown in the panel when `disabled` is true. */
  disabledMessage?: string
}

/**
 * What a cluster row does. A `'menu'` row opens its tools — with a `route` it also
 * links to its category page, without one (the host-configured groups) it is
 * popover-only. A `'link'` row goes to its `route` and opens nothing, so the
 * destination is required.
 */
export type ClusterDefinition =
  | (ClusterDefinitionBase & { behaviour?: 'menu'; route?: string })
  | (ClusterDefinitionBase & { behaviour: 'link'; route: string })

/**
 * Lightweight menu entry for tools that don't have a backend `ToolType` in the
 * protobuf enum (e.g. Color Converter, Contrast Checker). These appear in the
 * sidebar navigation cluster popover alongside regular tools, but are excluded
 * from the app-switch bar, category page cards, and the favorites system.
 */
export interface NavigationOnlyItem {
  title: string
  route: string
  icon: React.ReactNode
  order: number
}

export type Cluster = ClusterDefinition & {
  items: AppSwitchBarItem[]
  /** Items that only appear in the navigation sidebar, not in the app-switch bar or category pages. */
  navigationOnlyItems: NavigationOnlyItem[]
}

export interface ToolSection {
  clusters: Cluster[]
  unclusteredTools: AppSwitchBarItem[]
}

export function leftoverCategoryClusters(
  section: CategoryNavigationSection,
  clusters: Cluster[],
): Cluster[] {
  const listed = new Set(navigationSectionClusterIds(section))
  return clusters.filter(cluster => !listed.has(cluster.cluster as AppSwitchCluster))
}

export function useClusterDefinitions(): ClusterDefinition[] {
  const t = useStudioTranslate()
  const navigationConfig = useStudioNavigationConfig()
  const extraToolGroups = navigationConfig?.extraToolGroups
  const isMarketingDirectLink = useFeatureIsOn(FEATURE_DIRECT_LINK_MARKETING_WORKSPACE)

  const definitions: ClusterDefinition[] = [
    {
      cluster: 'image',
      label: t('studio.clusters.image.label'),
      icon: <Image01Icon />,
      route: '/image',
      allLabel: t('studio.clusters.image.all'),
    },
    {
      cluster: 'video',
      label: t('studio.clusters.video.label'),
      icon: <Tv03Icon />,
      route: ROUTES.AI_VIDEO_GENERATOR.ROOT,
      allLabel: t('studio.clusters.video.all'),
    },
    {
      cluster: 'audio',
      label: t('studio.clusters.audio.label'),
      icon: <VolumeMaxIcon />,
      route: '/audio',
      allLabel: t('studio.clusters.audio.all'),
    },
    {
      cluster: 'utilities',
      label: t('studio.clusters.utilities.label'),
      icon: <ClockRefreshIcon />,
      route: '/utilities',
      allLabel: t('studio.clusters.utilities.all'),
    },
    {
      cluster: 'entertainment',
      label: t('studio.clusters.entertainment.label'),
      icon: <FaceIdSquare />,
      route: '/entertainment',
      allLabel: t('studio.clusters.entertainment.all'),
    },
    {
      cluster: '3d',
      label: t('studio.clusters.3d.label'),
      icon: <Cube01Icon />,
      behaviour: 'link',
      route: ROUTES.THREED_WORKSPACE.ROOT,
      allLabel: t('studio.clusters.3d.all'),
    },
    isMarketingDirectLink
      ? {
          cluster: 'marketing',
          label: t('studio.clusters.marketing.label'),
          icon: <Announcement03Icon />,
          behaviour: 'link',
          route: ROUTES.MARKETING_STUDIO.ROOT,
          allLabel: t('studio.clusters.marketing.all'),
        }
      : {
          cluster: 'marketing',
          label: t('studio.clusters.marketing.label'),
          icon: <Announcement03Icon />,
          route: '/marketing',
          allLabel: t('studio.clusters.marketing.all'),
        },
    {
      cluster: 'crafting',
      label: t('studio.clusters.crafting.label'),
      icon: <ScissorsCut02Icon />,
      route: '/crafting',
      allLabel: t('studio.clusters.crafting.all'),
    },
  ]

  // Host-configured tool groups (e.g. the desktop "Local models" category)
  // contribute synthetic, category-page-less clusters. With no `route` they
  // render as a popover/sub-list trigger rather than linking to a category page,
  // and `allLabel` (never displayed without a route) mirrors the label.
  if (extraToolGroups) {
    extraToolGroups.forEach(group => {
      definitions.push({
        cluster: group.id,
        label: group.title,
        icon: group.icon,
        allLabel: group.title,
        disabled: group.enabled === false,
        disabledMessage: group.disabledMessage,
      })
    })
  }

  return definitions
}

/**
 * Returns navigation-only items grouped by cluster. Use this to add tools to
 * the sidebar menu that don't have a protobuf `ToolType` and therefore can't
 * be registered in `useAppSwitchItems()`.
 */
export function useNavigationOnlyItems(): Partial<Record<AppSwitchCluster, NavigationOnlyItem[]>> {
  const t = useStudioTranslate()
  const isFreemode = useIsFreemode()
  return React.useMemo(
    () => ({
      image: isFreemode
        ? []
        : [
            {
              title: t('studio.navigation_tools.image_outpainting'),
              route: ROUTES.IMAGE_OUTPAINTING.ROOT,
              icon: <Expand06Icon />,
              order: 1.05,
            },
            {
              title: t('studio.navigation_tools.image_inpainting'),
              route: ROUTES.IMAGE_INPAINTING.ROOT,
              icon: <InPaintingIcon />,
              order: 1.06,
            },
            {
              title: t('studio.navigation_tools.character_sheet'),
              route: ROUTES.CHARACTER_SHEET.ROOT,
              icon: <ImageUserIcon />,
              order: 1.1,
            },
          ],
      marketing: [
        {
          title: t('studio.navigation_tools.marketing_templates'),
          route: ROUTES.MARKETING_TEMPLATES.ROOT,
          icon: <Film01Icon />,
          // Negative keeps it first even when a host (freemode) remaps tool orders to 0-based indices.
          order: -1,
        },
      ],
      utilities: [
        {
          title: t('studio.navigation_tools.color_converter'),
          route: ROUTES.COLOR_CONVERTER.ROOT,
          icon: <DropperIcon />,
          order: 100,
        },
        {
          title: t('studio.navigation_tools.contrast_checker'),
          route: ROUTES.CONTRAST_CHECKER.ROOT,
          icon: <Contrast02Icon />,
          order: 101,
        },
      ],
      entertainment: [
        {
          title: t('studio.navigation_tools.moments'),
          route: ROUTES.MOMENTS.ROOT,
          icon: <Certificate02Icon />,
          // Above the entertainment tools (max 23) so it stays last now that order drives position.
          order: 100,
        },
      ],
    }),
    [isFreemode, t],
  )
}

/**
 * Builds the navigation cluster for a host-configured tool group. An enabled
 * group surfaces its matched public tools and any singular (hidden-route) items;
 * a disabled group renders a message-only panel and contributes no rows.
 * Returns `null` when an enabled group has nothing to show.
 */
function buildToolGroupCluster(
  group: NavigationToolGroupConfig,
  clusterDefinitions: ClusterDefinition[],
  clusteredItems: Partial<Record<NavigationClusterId, AppSwitchBarItem[]>>,
): Cluster | null {
  const definition = clusterDefinitions.find(def => def.cluster === group.id)
  if (!definition) {
    return null
  }
  const items = clusteredItems[group.id] ?? []
  const groupNavigationOnlyItems: NavigationOnlyItem[] =
    definition.disabled || !group.singularItems
      ? []
      : group.singularItems.map((item, index) => ({
          title: item.title,
          route: item.route,
          icon: item.icon,
          // Desktop "Local models" (only host group with singular items): keep them after their
          // matched tools (small catalog orders) in the order-sorted popover; `+ index` keeps order.
          order: 1000 + index,
        }))
  if (definition.disabled || items.length > 0 || groupNavigationOnlyItems.length > 0) {
    return { ...definition, items, navigationOnlyItems: groupNavigationOnlyItems }
  }
  return null
}

/**
 * Host-configured tool groups opted into the `'after-home'` placement. These
 * render as their own grouped rows directly under Home (above pinned tools and
 * section headings) instead of being folded into their category section.
 */
export function useAfterHomeClusters(): Cluster[] {
  const clusterDefinitions = useClusterDefinitions()
  const clusteredItems = useClusteredItems()
  const navigationConfig = useStudioNavigationConfig()
  const extraToolGroups = navigationConfig?.extraToolGroups

  return React.useMemo(() => {
    if (!extraToolGroups) {
      return []
    }
    return extraToolGroups
      .filter(group => group.placement === 'after-home')
      .map(group => buildToolGroupCluster(group, clusterDefinitions, clusteredItems))
      .filter((cluster): cluster is Cluster => cluster !== null)
  }, [clusterDefinitions, clusteredItems, extraToolGroups])
}

function groupItemsByCluster(
  items: AppSwitchBarItem[],
): Partial<Record<NavigationClusterId, AppSwitchBarItem[]>> {
  const clusters: Partial<Record<NavigationClusterId, AppSwitchBarItem[]>> = {}
  items.forEach(item => {
    if (item.cluster) {
      const cluster = clusters[item.cluster] ?? []
      clusters[item.cluster] = cluster
      cluster.push(item)
    }
  })
  return clusters
}

/**
 * Resolves a cluster's tools for a host: keeps only the tools it allows and
 * orders them by their position in `visibleToolKeys`. Hosts that set it (only
 * freemode today) control which tools show and their order; hosts without it
 * keep the global catalog order.
 */
export function getVisibleClusterTools(
  items: AppSwitchBarItem[],
  allowedKeys: number[] | undefined,
): AppSwitchBarItem[] {
  if (!allowedKeys) {
    return items.toSorted((a, b) => a.order - b.order)
  }
  return items
    .filter(item => allowedKeys.includes(item.toolType))
    .map(item => ({ ...item, order: allowedKeys.indexOf(item.toolType) }))
    .toSorted((a, b) => a.order - b.order)
}

/**
 * Catalogue tools by route, so a host-declared singular entry pointing at a tool carries that
 * tool's favourite state instead of rendering as a plain link.
 */
export function useToolsByRoute(): Map<string, AppSwitchBarItem> {
  const appSwitchItems = useAppSwitchItems()

  return React.useMemo(
    () => new Map(appSwitchItems.map(item => [item.route, item])),
    [appSwitchItems],
  )
}

export function useNavigationSections(): NavigationSectionConfig[] {
  const navigationConfig = useStudioNavigationConfig()
  return navigationConfig?.sections ?? NAVIGATION_SECTIONS
}

export function useClusteredItems(): Partial<Record<NavigationClusterId, AppSwitchBarItem[]>> {
  const appSwitchItems = useAppSwitchItems()
  const navigationConfig = useStudioNavigationConfig()
  const extraToolGroups = navigationConfig?.extraToolGroups

  return React.useMemo(() => {
    const clusters = groupItemsByCluster(appSwitchItems)

    if (!extraToolGroups) {
      return clusters
    }

    // Host-configured groups surface existing tools matched by route. An enabled
    // group lifts its tools into its own row and claims them from their original
    // clusters so they aren't duplicated. A disabled group keeps its tools in
    // their original clusters and renders a message-only panel instead.
    extraToolGroups.forEach(group => {
      if (group.enabled === false) {
        clusters[group.id] = []
        return
      }
      const groupItems = appSwitchItems.filter(item => group.toolRoutes.includes(item.route))
      if (groupItems.length === 0) {
        return
      }
      clusters[group.id] = groupItems
      groupItems.forEach(groupItem => {
        const sourceCluster = groupItem.cluster
        if (sourceCluster && clusters[sourceCluster]) {
          clusters[sourceCluster] = clusters[sourceCluster].filter(
            item => item.route !== groupItem.route,
          )
        }
      })
    })

    return clusters
  }, [appSwitchItems, extraToolGroups])
}

/**
 * Tools a host's navigation actually offers: those in the clusters it declares, plus the ones its
 * singular rows and tool groups point at. `undefined` for a host with no navigation of its own,
 * which offers the whole catalog.
 */
export function useOfferedToolTypes(): Set<number> | undefined {
  const navigationConfig = useStudioNavigationConfig()
  const clusteredItems = useClusteredItems()
  const toolsByRoute = useToolsByRoute()

  return React.useMemo(() => {
    if (!navigationConfig) {
      return undefined
    }

    const offered = new Set<number>()
    const offerRoute = (route: string) => {
      const tool = toolsByRoute.get(route)
      if (tool) {
        offered.add(tool.toolType)
      }
    }

    navigationConfig.sections.forEach(section => {
      if (section.kind !== 'category') {
        return
      }
      section.entries.forEach(entry => {
        if (entry.kind === 'singular') {
          offerRoute(entry.route)
          return
        }
        getVisibleClusterTools(
          clusteredItems[entry.cluster] ?? [],
          navigationConfig.visibleToolKeys?.[entry.cluster],
        ).forEach(tool => offered.add(tool.toolType))
      })
    })
    navigationConfig.extraToolGroups?.forEach(group => group.toolRoutes.forEach(offerRoute))

    return offered
  }, [navigationConfig, clusteredItems, toolsByRoute])
}

export function usePinnedToolsByCluster() {
  const appSwitchItems = useAppSwitchItems()
  const clusterDefinitions = useClusterDefinitions()
  const { favoriteToolTypes } = useFavoriteToolTypes()
  const offeredToolTypes = useOfferedToolTypes()

  return React.useMemo(() => {
    const sidebarClusters = new Set(clusterDefinitions.map(definition => definition.cluster))
    const pinnedTools = appSwitchItems.filter(
      item =>
        item.cluster !== undefined &&
        sidebarClusters.has(item.cluster) &&
        favoriteToolTypes.has(item.toolType) &&
        (!offeredToolTypes || offeredToolTypes.has(item.toolType)),
    )

    if (pinnedTools.length === 0) {
      return null
    }

    return groupItemsByCluster(pinnedTools)
  }, [appSwitchItems, clusterDefinitions, favoriteToolTypes, offeredToolTypes])
}

export function useToolSections(): ToolSection[] {
  const appSwitchItems = useAppSwitchItems()
  const clusterDefinitions = useClusterDefinitions()
  const clusteredItems = useClusteredItems()
  const navigationOnlyItems = useNavigationOnlyItems()
  const navigationConfig = useStudioNavigationConfig()

  const sections = navigationConfig?.sections ?? NAVIGATION_SECTIONS
  const visibleToolKeys = navigationConfig?.visibleToolKeys
  const extraToolGroups = navigationConfig?.extraToolGroups

  return React.useMemo(() => {
    return sections.map(section => {
      if (section.kind !== 'category') {
        return { clusters: [], unclusteredTools: [] }
      }

      const clusters: Cluster[] = navigationSectionClusterIds(section)
        .map(cluster => {
          const definition = clusterDefinitions.find(def => def.cluster === cluster)
          if (!definition) {
            return null
          }
          const items = getVisibleClusterTools(
            clusteredItems[cluster] ?? [],
            visibleToolKeys?.[cluster],
          )
          return {
            ...definition,
            items,
            navigationOnlyItems: navigationOnlyItems[cluster] ?? [],
          }
        })
        .filter(
          (cluster): cluster is Cluster =>
            cluster !== null &&
            (cluster.items.length > 0 || cluster.navigationOnlyItems.length > 0),
        )

      // Host-configured tool groups: surface each as its own row within the
      // section matching its category. Groups opted into the `'after-home'`
      // placement are rendered separately (see `useAfterHomeClusters`) and are
      // excluded here so they aren't duplicated inside their category section.
      if (extraToolGroups) {
        extraToolGroups
          .filter(group => group.category === section.category && group.placement !== 'after-home')
          .forEach(group => {
            const cluster = buildToolGroupCluster(group, clusterDefinitions, clusteredItems)
            if (cluster) {
              clusters.push(cluster)
            }
          })
      }

      const unclusteredTools = appSwitchItems
        .filter(item => item.category === section.category && !item.cluster)
        .toSorted((a, b) => a.order - b.order)

      return {
        clusters,
        unclusteredTools,
      }
    })
  }, [
    appSwitchItems,
    clusterDefinitions,
    clusteredItems,
    extraToolGroups,
    navigationOnlyItems,
    sections,
    visibleToolKeys,
  ])
}
